import React from "react";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";

import { colors } from "@/theme/overrides/colors";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledFileIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  useStyles,
} from "../utils/Icons";

type FileArguments = {
  label: string;
  maximumNumberOfFiles: number;
  maximumFileSize: number;
};

interface Props {
  element: FormElement<FileArguments>;
}

const FormFile: React.FC<Props> = observer(({ element }) => {
  const maximumNumberOfFiles = [2, 4, 6, 8, 10];
  const maximumFileSize = [10, 20, 30, 40, 50];
  const editing = useEditing(element);
  const { classes } = useStyles();

  return (
    <>
      {!editing.value ? (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon
            className={clsx(["styled-drag-indicator"])}
          />
          <div className={classes.columnContent}>
            <Typography
              variant="h6"
              color="textPrimary"
              style={{ marginBottom: 12 }}
            >
              {element.arguments.label || "Files label"}
            </Typography>

            {/* //DropZone input here*/}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              style={{ backgroundColor: colors.light }}
              padding={8}
            >
              <StyledFileIcon />
              <>
                <Typography variant="body2" color="textSecondary">
                  Drop your photos and videos here or upload from your device
                </Typography>
              </>
            </Box>
          </div>

          <div className={clsx([classes.editIcons, "edit-icons-container"])}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon onClick={() => element.remove()} />
          </div>
        </Box>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.columnContent}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Media label
              </Typography>
              <TextField
                value={element.arguments.label}
                onChange={(e) => element.setArgument("label", e.target.value)}
              />

              <Box display="flex" flexDirection="row" marginTop={4}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  marginRight={4}
                >
                  <Typography variant="body2" color="textSecondary">
                    Maximum number of files
                  </Typography>
                  <Select
                    fullWidth
                    variant="outlined"
                    style={{ marginRight: 8, marginTop: 8, width: "100%" }}
                    id="simple-select"
                    value={
                      element.arguments.maximumNumberOfFiles ??
                      maximumNumberOfFiles[0]
                    }
                    onChange={(e) =>
                      element.setArgument(
                        "maximumNumberOfFiles",
                        e.target.value as number
                      )
                    }
                  >
                    {maximumNumberOfFiles.map((e, i) => (
                      <MenuItem value={e} key={i}>
                        {e}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  marginRight={4}
                >
                  <Typography variant="body2" color="textSecondary">
                    Maximum file size
                  </Typography>
                  <Select
                    fullWidth
                    variant="outlined"
                    style={{ marginRight: 8, marginTop: 8, width: "100%" }}
                    id="simple-select"
                    value={
                      element.arguments.maximumFileSize || maximumFileSize[0]
                    }
                    onChange={(e) =>
                      element.setArgument(
                        "maximumFileSize",
                        e.target.value as number
                      )
                    }
                    endAdornment={
                      <Typography
                        variant="body1"
                        color="textPrimary"
                        style={{ marginRight: 12 }}
                      >
                        MB
                      </Typography>
                    }
                  >
                    {maximumFileSize.map((e, i) => (
                      <MenuItem value={e} key={i}>
                        {e}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>

              <StyledHorizontalDivider />
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        aria-label="Required"
                        checked={element.required}
                        onChange={(e) => element.setRequired(e.target.checked)}
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label="Required"
                  />
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button
                    className={classes.saveChangesButton}
                    onClick={editing.stop}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Box>
          </Box>
          <div className={classes.editIcons}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon />
          </div>
        </Box>
      )}
    </>
  );
});

export default FormFile;
